// Reddit Conversation Simulator
// Generate realistic Reddit conversations using AI

import { apiCall } from './api';

class RedditSimulator {
  constructor() {
    this.adjectives = [
      'quick', 'silent', 'bright', 'calm', 'deep', 'fresh', 'happy', 'kind',
      'lazy', 'neat', 'old', 'proud', 'quiet', 'red', 'safe', 'tall',
      'wise', 'young', 'bold', 'cool', 'dark', 'fast', 'huge', 'loud'
    ];

    this.names = [
      'alex', 'bob', 'charlie', 'dana', 'evan', 'fiona', 'george', 'hannah',
      'ivan', 'julia', 'kevin', 'luna', 'mike', 'nina', 'oscar', 'penny',
      'quinn', 'ruby', 'sam', 'tina', 'uma', 'victor', 'wendy', 'xander'
    ];

    this.subreddits = [
      'entrepreneur', 'startups', 'smallbusiness', 'marketing', 'saas',
      'freelance', 'consulting', 'business', 'digitalnomad', 'remotework',
      'productivity', 'customerservice', 'sales', 'growthstrategy', 'indiehackers'
    ];
  }

  // Generate realistic conversations about pain points
  async generateConversations(selectedSegments, marketContext) {
    console.log('🔍 Searching Reddit for relevant conversations...');

    // Extract pain points from segments
    const painPoints = this.extractPainPoints(selectedSegments);

    const prompt = `You're analyzing Reddit conversations about businesses in the "${marketContext}" market. Based on these specific pain points, generate realistic analysis:

MARKET SEGMENTS ANALYZED:
${painPoints.map(point => `- ${point}`).join('\n')}

Generate a detailed analysis with SPECIFIC examples, costs, and real business struggles. Don't be generic.

SUMMARY:
Write 2-3 sentences about the main themes you found across 12-15 Reddit conversations about this market.

PAIN POINTS:
List 8-10 SHORT, specific pain points (1-2 lines each) like:
- Customer acquisition costs averaging $150-300 per customer
- 6-month sales cycles killing cash flow
- Manual data entry wastes 3 hours daily
- No integration between key tools
- 40% customer churn after first month
- Competition undercuts pricing by 20%

Keep each pain point to 1-2 sentences maximum.

OPPORTUNITIES:
List 5-6 SPECIFIC opportunities like:
- Automated verification tools using AI image recognition
- Platform connecting neighborhood recycling champions with municipalities
- SaaS tool for tracking recycling participation across demographics
- Consulting services for sustainability policy implementation
- Mobile app for real-time recycling submission validation
- Data analytics dashboard for municipal recycling insights

NOTABLE QUOTES:
Write 3 realistic Reddit quotes (3-5 lines each) with:
- Spelling mistakes and typos
- Contractions (don't, can't, won't, it's, they're)
- Casual Reddit language
- Real frustration/emotion
- Specific examples/numbers

Format quotes like:
"been trying to get customers for 8 months now and its just not working. spent like 5k on ads and got maybe 10 leads that didnt convert. anyone else struggling with this??" - u/quickbob247 in r/entrepreneur

"cant believe how much time i waste on [specific task]. literally 3 hours a day doing something that should take 30 mins but theres no good tools for it" - u/silentmike88 in r/business

Use generic Reddit usernames like: adjective + name + number (e.g., quickbob247, silentmike88, brightluna45)

Keep everything simple and readable - NO MARKDOWN FORMATTING like ** or ###. Just plain text with bullet points using dashes (-).`;

    try {
      const response = await apiCall(prompt, {
        temperature: 0.7,
        maxTokens: 2000 // Reduced for faster generation
      });

      return this.parseConversations(response);
    } catch (error) {
      console.error('Error generating conversations:', error);
      throw new Error('Failed to generate Reddit conversations');
    }
  }

  // Extract pain points from selected segments
  extractPainPoints(segments) {
    const painPoints = [];

    segments.forEach(segment => {
      if (segment.approach === 'PROBLEM/PAIN POINT') {
        painPoints.push(segment.subcategory);
      }

      // Extract from other approaches
      const painKeywords = [
        'problem', 'pain', 'struggle', 'challenge', 'issue', 'difficulty',
        'frustration', 'hard', 'expensive', 'time consuming', 'manual'
      ];

      const categoryText = `${segment.category} ${segment.subcategory}`.toLowerCase();
      if (painKeywords.some(keyword => categoryText.includes(keyword))) {
        painPoints.push(segment.subcategory);
      }
    });

    // Add some general business pain points
    const generalPains = [
      'customer acquisition costs',
      'finding qualified leads',
      'scaling operations',
      'managing remote teams',
      'pricing strategy'
    ];

    painPoints.push(...generalPains);
    return [...new Set(painPoints)]; // Remove duplicates
  }

  // Parse the structured AI response
  parseConversations(response) {
    try {
      console.log('Parsing AI response:', response);

      // Extract sections using regex
      const summaryMatch = response.match(/SUMMARY:\s*(.*?)(?=\n\s*PAIN POINTS:|$)/s);
      const painPointsMatch = response.match(/PAIN POINTS:\s*(.*?)(?=\n\s*OPPORTUNITIES:|$)/s);
      const opportunitiesMatch = response.match(/OPPORTUNITIES:\s*(.*?)(?=\n\s*NOTABLE QUOTES:|$)/s);
      const quotesMatch = response.match(/NOTABLE QUOTES:\s*(.*?)$/s);

      // Parse quotes to extract sample conversations
      const conversations = [];
      if (quotesMatch) {
        const quotesText = quotesMatch[1];
        // Look for quotes in format: "quote text" - u/username in r/subreddit
        const quoteMatches = quotesText.match(/"([^"]+)"\s*-\s*u\/(\w+)\s+in\s+r\/(\w+)/g);

        if (quoteMatches) {
          quoteMatches.slice(0, 3).forEach((match, index) => {
            const parts = match.match(/"([^"]+)"\s*-\s*u\/(\w+)\s+in\s+r\/(\w+)/);
            if (parts) {
              conversations.push({
                title: this.generateTitleFromQuote(parts[1]),
                author: parts[2],
                subreddit: parts[3],
                content: parts[1],
                posted: this.getRandomTimestamp(),
                upvotes: Math.floor(Math.random() * 100) + 5,
                url: `https://reddit.com/r/${parts[3]}/comments/${this.generatePostId()}`
              });
            }
          });
        }
      }

      const fallback = this.createFallbackAnalysis('');

      return {
        summary: summaryMatch ? summaryMatch[1].trim() : fallback.summary,
        keyPainPoints: painPointsMatch ? painPointsMatch[1].trim() : fallback.keyPainPoints,
        opportunities: opportunitiesMatch ? opportunitiesMatch[1].trim() : fallback.opportunities,
        demographics: 'Primarily business owners, entrepreneurs, and professionals discussing operational challenges.',
        notableQuotes: quotesMatch ? quotesMatch[1].trim() : fallback.notableQuotes,
        rawConversations: conversations.length > 0 ? conversations : fallback.rawConversations
      };

    } catch (error) {
      console.error('Error parsing analysis:', error);
      return this.createFallbackAnalysis(response);
    }
  }

  // Enhance conversations with realistic data
  enhanceConversations(conversations) {
    return conversations.map((conv, index) => {
      // Add realistic data if missing
      if (!conv.author) {
        conv.author = this.getRandomUsername();
      }
      if (!conv.subreddit) {
        conv.subreddit = this.getRandomSubreddit();
      }
      if (!conv.posted) {
        conv.posted = this.getRandomTimestamp();
      }
      if (!conv.upvotes) {
        conv.upvotes = Math.floor(Math.random() * 100) + 5;
      }

      // Enhance comments
      if (conv.comments) {
        conv.comments = conv.comments.map(comment => ({
          ...comment,
          author: comment.author || this.getRandomUsername(),
          posted: comment.posted || this.getRandomTimestamp(),
          upvotes: comment.upvotes || Math.floor(Math.random() * 30) + 1
        }));
      }

      // Add URL
      conv.url = `https://reddit.com/r/${conv.subreddit}/comments/${this.generatePostId()}/${this.slugify(conv.title)}`;

      return conv;
    });
  }

  // Create fallback conversations from plain text
  createFallbackConversations(text) {
    const lines = text.split('\n').filter(line => line.trim().length > 20);
    const conversations = [];

    for (let i = 0; i < Math.min(lines.length, 8); i++) {
      const content = lines[i].trim();
      conversations.push({
        title: this.generateTitleFromContent(content),
        subreddit: this.getRandomSubreddit(),
        author: this.getRandomUsername(),
        posted: this.getRandomTimestamp(),
        upvotes: Math.floor(Math.random() * 100) + 5,
        content: content,
        url: `https://reddit.com/r/${this.getRandomSubreddit()}/comments/${this.generatePostId()}`,
        comments: this.generateRandomComments()
      });
    }

    return conversations;
  }

  // Generate title from content
  generateTitleFromContent(content) {
    const words = content.split(' ').slice(0, 8);
    return words.join(' ') + (content.length > 50 ? '...' : '');
  }

  // Generate title from quote
  generateTitleFromQuote(quote) {
    // Take first part of quote and make it title-like
    const words = quote.split(' ').slice(0, 10).join(' ');

    // Common Reddit title patterns
    const patterns = [
      `Anyone else dealing with ${words.toLowerCase()}?`,
      `How do you handle ${words.toLowerCase()}?`,
      `Struggling with ${words.toLowerCase()}`,
      `${words} - need advice`,
      `Is it just me or ${words.toLowerCase()}?`
    ];

    return patterns[Math.floor(Math.random() * patterns.length)];
  }

  // Generate random comments
  generateRandomComments() {
    const comments = [
      "I've experienced this exact same issue. It's so frustrating!",
      "Have you tried reaching out to other business owners? Sometimes collaboration helps.",
      "This is why I switched to a different approach entirely.",
      "The costs keep going up but the results stay the same.",
      "I think the real problem is that most solutions are designed for bigger companies."
    ];

    const numComments = Math.floor(Math.random() * 4) + 2;
    return comments.slice(0, numComments).map(content => ({
      author: this.getRandomUsername(),
      posted: this.getRandomTimestamp(),
      upvotes: Math.floor(Math.random() * 20) + 1,
      content
    }));
  }

  // Helper functions
  getRandomUsername() {
    const adjective = this.adjectives[Math.floor(Math.random() * this.adjectives.length)];
    const name = this.names[Math.floor(Math.random() * this.names.length)];
    const number = Math.floor(Math.random() * 999) + 1;
    return `${adjective}${name}${number}`;
  }

  getRandomSubreddit() {
    return this.subreddits[Math.floor(Math.random() * this.subreddits.length)];
  }

  getRandomTimestamp() {
    const now = new Date();
    const daysAgo = Math.floor(Math.random() * 30); // Random day in last 30 days
    const hoursAgo = Math.floor(Math.random() * 24);
    const minutesAgo = Math.floor(Math.random() * 60);

    const timestamp = new Date(now);
    timestamp.setDate(timestamp.getDate() - daysAgo);
    timestamp.setHours(timestamp.getHours() - hoursAgo);
    timestamp.setMinutes(timestamp.getMinutes() - minutesAgo);

    return timestamp.toISOString();
  }

  generatePostId() {
    return Math.random().toString(36).substr(2, 9);
  }

  slugify(text) {
    return text.toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  // Generate quotes from conversations
  generateQuotesFromConversations(conversations) {
    if (conversations.length === 0) {
      return 'Various business owners shared their experiences and challenges.';
    }

    return conversations.slice(0, 3).map(conv =>
      `"${conv.content}" - u/${conv.author} in r/${conv.subreddit}`
    ).join('\n\n');
  }

  // Create fallback analysis if parsing fails
  createFallbackAnalysis(response) {
    return {
      summary: 'Found multiple conversations discussing business challenges and pain points across various subreddits.',
      keyPainPoints: `- Customer acquisition costs averaging $200-400 per customer
- 4-6 month sales cycles killing cash flow
- Manual processes waste 2-3 hours daily
- Limited integration between tools
- High customer churn in first 90 days
- Competition undercuts pricing by 15-25%
- Scaling requires expensive additional staff
- Data collection and reporting is manual`,
      opportunities: `- Automated workflow tools to reduce manual tasks
- CRM integration platforms for better data flow
- Customer retention software with predictive analytics
- Competitive pricing intelligence tools
- Sales cycle acceleration consulting services
- Staff training and optimization programs`,
      demographics: 'Primarily business owners, entrepreneurs, and professionals discussing operational challenges.',
      notableQuotes: 'Users frequently discuss frustrations with current solutions and desire for better alternatives.',
      rawConversations: this.generateFallbackConversations()
    };
  }

  // Generate fallback conversations if needed
  generateFallbackConversations() {
    return [
      {
        title: "Customer acquisition costs are killing my startup",
        author: this.getRandomUsername(),
        subreddit: "entrepreneur",
        content: "been struggling with CAC for months. spending $200 to get customers worth $50. any advice??",
        posted: this.getRandomTimestamp(),
        upvotes: 45,
        url: `https://reddit.com/r/entrepreneur/comments/${this.generatePostId()}`
      },
      {
        title: "How do you scale operations without burning out?",
        author: this.getRandomUsername(),
        subreddit: "smallbusiness",
        content: "growing fast but cant keep up. working 80hr weeks and still falling behind. what am i missing?",
        posted: this.getRandomTimestamp(),
        upvotes: 67,
        url: `https://reddit.com/r/smallbusiness/comments/${this.generatePostId()}`
      },
      {
        title: "Remote team management is harder than expected",
        author: this.getRandomUsername(),
        subreddit: "remotework",
        content: "thought managing remote teams would be easy. communication gaps and productivity issues everywhere.",
        posted: this.getRandomTimestamp(),
        upvotes: 32,
        url: `https://reddit.com/r/remotework/comments/${this.generatePostId()}`
      }
    ];
  }

  // Analyze conversations - this is the main method called by the component
  analyzeConversations(conversations) {
    // The parseConversations method already returns the analysis
    // This method is kept for compatibility with the component
    return conversations;
  }
}

export default RedditSimulator;